import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

import { getConfig } from '../translations/getTranslationConfig';
import { translationKeys } from '../translations/translationKeys';
import type { Translations } from '../translations/translationsTypes';
import logger from '../utils/logger.ts';
import { useMergedConfig } from './MergedConfigProvider';

type TranslationsContextValue = {
  translations: Translations;
  isLoading: boolean;
  error: Error | null;
  translate: (key: translationKeys) => string;
};

const TranslationsContext = createContext<TranslationsContextValue | undefined>(undefined);

export function TranslationsProvider({
  children,
  prefetchedPromise,
}: {
  children: ReactNode;
  prefetchedPromise?: Promise<{ translations: Translations }>;
}) {
  const { lang } = useMergedConfig();
  const [translations, setTranslations] = useState<Translations>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const prefetchUsedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const fetchTranslations = async () => {
      setIsLoading(true);
      setError(null);

      // The prefetch was issued for the initial language only; later changes must refetch.
      const promise =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        !prefetchUsedRef.current && prefetchedPromise ? prefetchedPromise : getConfig(lang as any);
      prefetchUsedRef.current = true;

      try {
        const { translations: fetched } = await promise;
        if (cancelled) return;
        setTranslations(fetched);
      } catch (err: unknown) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchTranslations();

    return () => {
      cancelled = true;
    };
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps -- prefetchedPromise is intentionally excluded: it's only used on first mount

  const translate = (key: translationKeys): string => {
    const match =
      translations.find((t) => t.keyName === key && t.language === lang) ?? translations.find((t) => t.keyName === key);

    if (match?.translation) {
      return match.translation;
    }

    // Components can mount before the fetch settles, so an empty set isn't a real miss.
    if (isLoading || translations.length === 0) {
      return `!!${key}`;
    }

    logger.error(`Translation not found for key: ${key} in lang: ${lang}`);
    return `!!${key}`;
  };

  return (
    <TranslationsContext.Provider value={{ translations, isLoading, error, translate }}>
      {children}
    </TranslationsContext.Provider>
  );
}

export function useTranslationsContext(): TranslationsContextValue {
  const context = useContext(TranslationsContext);
  if (!context) {
    throw new Error('useTranslationsContext must be used within a TranslationsProvider');
  }
  return context;
}
